import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useParams } from "react-router-dom"
import Swal from "sweetalert2"
import bharatAtsLogo from "../../assets/images/ats-logo.png"

const recommendationOptions = [
  { value: "", label: "Select recommendation" },
  { value: "strong_hire", label: "Strong Hire" },
  { value: "hire", label: "Hire" },
  { value: "hold", label: "Hold" },
  { value: "no_hire", label: "No Hire" },
  { value: "candidate_no_show", label: "Candidate Did Not Attend" },
]

const recommendationLabelMap = recommendationOptions.reduce(
  (acc, item) => {
    acc[item.value] = item.label
    return acc
  },
  {}
)

const getRecommendationLabel = value =>
  recommendationLabelMap[value] || value || ""

const demoTokens = ["demo", "dummy", "preview"]

const demoFeedbackDetails = {
  feedback_id: "demo",
  job_candidate_id: "demo-candidate-1",
  job_title: "Senior Python Developer",
  job_number: "JOB-2026-1042",
  candidate_name: "Anwar Moghal",
  candidate_email: "anwar@example.com",
  candidate_phone: "9876543210",
  interview_type: "Interview",
  interview_sub_type: "Video Interview",
  interview_date: "2026-08-05",
  start_time: "11:30",
  end_time: "12:00",
  subject: "Interview Schedule with Senior Python Developer",

  panel_id: "demo-panel-1",
  panel_name: "Panel Reviewer",
  panel_email: "panel@example.com",

  panel_options: [
    {
      panel_id: "demo-panel-1",
      name: "Panel Reviewer",
      email: "panel@example.com",
      submitted_at: null,
    },
    {
      panel_id: "demo-panel-2",
      name: "Technical Lead",
      email: "techlead@example.com",
      submitted_at: null,
    },
  ],

  candidate_options: [
    {
      job_candidate_id: "demo-candidate-1",
      candidate_id: "demo-candidate-1",
      name: "Anwar Moghal",
      feedback_id: "demo",
      submitted_at: null,
    },
    {
      job_candidate_id: "demo-candidate-2",
      candidate_id: "demo-candidate-2",
      name: "G PRANAYA",
      feedback_id: "demo",
      submitted_at: null,
    },
  ],

  feedback: "",
  rating: "",
  recommendation: "",
  submitted_at: null,
  company_name: "Pranathi Software Services",
}

/*
 * Build panel options — preserves panel_id
 */
const normalizePanelOptions = details => {
  const rawPanels =
    details?.panel_options ||
    details?.panels ||
    details?.panel_members ||
    details?.interview_panel ||
    details?.panel_names ||
    []

  const panelList = Array.isArray(rawPanels)
    ? rawPanels
    : [rawPanels]

  const options = panelList
    .map(item => {
      if (typeof item === "string") {
        return { panel_id: null, name: item.trim(), email: "" }
      }

      const name =
        item?.name ||
        item?.panel_name ||
        item?.full_name ||
        item?.username ||
        item?.label ||
        ""

      const email =
        item?.email ||
        item?.panel_email ||
        item?.user_email ||
        item?.value ||
        ""

      return {
        panel_id: item?.panel_id ?? null,
        name: String(name).trim(),
        email: String(email || "").trim(),
      }
    })
    .filter(item => item.name)

  if (
    details?.panel_name &&
    !options.some(item => item.name === details.panel_name)
  ) {
    options.unshift({
      panel_id: details.panel_id ?? null,
      name: details.panel_name,
      email: details.panel_email || "",
    })
  }

  return options
}

const DetailItem = ({ label, value }) => (
  <div
    style={{
      minWidth: 0,
      background: "#f8fafc",
      border: "1px solid #e6edf5",
      borderRadius: 8,
      padding: "14px 16px",
      minHeight: 78,
    }}
  >
    <div
      style={{
        fontSize: 10,
        fontWeight: 800,
        color: "#61738f",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>

    <div
      style={{
        marginTop: 7,
        fontSize: 13,
        color: "#0f172a",
        fontWeight: 700,
        wordBreak: "break-word",
        lineHeight: 1.35,
      }}
    >
      {value || "--"}
    </div>
  </div>
)

/*
 * Read-only star display for previous submission reference
 */
const StarDisplay = ({ rating, size = 16 }) => {
  const n = parseInt(rating, 10)
  if (!n || n < 1)
    return <span style={{ color: "#94a3b8", fontSize: 12 }}>Not rated yet</span>
  return (
    <span>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          style={{ color: i <= n ? "#f59e0b" : "#d1d5db", fontSize: size }}
        >
          &#9733;
        </span>
      ))}{" "}
      <span style={{ fontSize: 12, color: "#64748b" }}>
        ({n}/5)
      </span>
    </span>
  )
}

/*
 * Card for a single candidate in the "all candidates" preview strip.
 * Shows name, submitted badge, and (if available) their rating —
 * so the panel can scan everyone before picking who to work on.
 */
const CandidateCard = ({ candidate, isSelected, onSelect }) => {
  const isSubmitted = Boolean(candidate.submitted_at)

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 6,
        minWidth: 190,
        maxWidth: 240,
        flex: "1 1 190px",
        textAlign: "left",
        cursor: "pointer",
        background: isSelected ? "#eaf4fb" : "#f8fafc",
        border: isSelected ? "2px solid #0e527d" : "1px solid #e6edf5",
        borderRadius: 8,
        padding: "12px 14px",
        transition: "background 0.12s, border-color 0.12s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: 8,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: "#0f172a",
            wordBreak: "break-word",
          }}
        >
          {candidate.name}
        </span>

        {isSubmitted && (
          <span
            style={{
              flexShrink: 0,
              background: "#dcfce7",
              color: "#166534",
              border: "1px solid #86efac",
              borderRadius: 999,
              padding: "1px 7px",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            ✅ Done
          </span>
        )}
      </div>

      <StarDisplay rating={candidate.rating} size={13} />

      {isSubmitted &&
        (candidate.recommendation_display || candidate.recommendation) && (
        <span style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>
          {getRecommendationLabel(
            candidate.recommendation_display || candidate.recommendation
          )}
        </span>
      )}
    </button>
  )
}

const InterviewFeedbackPage = () => {
  const { token } = useParams()

  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [switchingCandidate, setSwitchingCandidate] = useState(false)

  /*
   * { [job_candidate_id]: isoTimestamp }
   * Tracks candidates submitted this session so the ✅ badge
   * appears immediately without waiting for a server refresh.
   */
  const [localSubmitted, setLocalSubmitted] = useState({})

  /*
   * Previously submitted feedback for the selected candidate.
   * Shown as a blue reference block above the form.
   */
  const [previousFeedback, setPreviousFeedback] = useState(null)

  const [selectedCandidateId, setSelectedCandidateId] = useState("")

  /*
   * Panel is keyed by panel_id (or name as fallback when panel_id
   * is absent) so we can match accurately.
   */
  const [selectedPanelId, setSelectedPanelId] = useState("")

  const [form, setForm] = useState({
    feedback: "",
    rating: "",
    recommendation: "",
  })

  const isDemoToken = demoTokens.includes(
    String(token || "").toLowerCase()
  )

  /*
   * Reset form fields + clear the previous-submission reference
   */
  const resetForm = () => {
    setForm({ feedback: "", rating: "", recommendation: "" })
    setPreviousFeedback(null)
  }

  /*
   * Hydrate component state from an API response object.
   * Called on initial load and optionally after candidate change.
   */
  const applyFeedbackDetails = (data, showAlreadySubmittedAlert = false) => {
    setDetails(data)

    /*
     * Step 1 — default to FIRST candidate
     */
    const candidates = Array.isArray(data?.candidate_options)
      ? data.candidate_options
      : []

    let candidateId = ""
    if (candidates.length > 0) {
      candidateId = String(candidates[0].job_candidate_id)
    } else if (data?.job_candidate_id != null) {
      candidateId = String(data.job_candidate_id)
    }
    setSelectedCandidateId(candidateId)

    /*
     * Step 2 — pre-select panel using panel_id from the top-level
     * response (the panel whose magic link was clicked).
     */
    const panels = normalizePanelOptions(data)
    let panelId = ""

    if (data?.panel_id) {
      const matched = panels.find(
        p => String(p.panel_id) === String(data.panel_id)
      )
      if (matched) panelId = String(matched.panel_id)
    }

    if (!panelId && panels.length === 1) {
      panelId =
        panels[0].panel_id != null
          ? String(panels[0].panel_id)
          : panels[0].name
    }

    setSelectedPanelId(panelId)

    /*
     * Show previous submission as reference if the top-level record
     * was already submitted
     */
    if (data?.submitted_at) {
      setPreviousFeedback({
        feedback: data?.feedback || "",
        rating: data?.rating ?? "",
        recommendation_display: getRecommendationLabel(
          data?.recommendation_display || data?.recommendation
        ),
        panel_name: data?.panel_name || "",
        panel_email: data?.panel_email || "",
        submitted_at: data.submitted_at,
      })

      if (showAlreadySubmittedAlert) {
        Swal.fire({
          icon: "info",
          title: "Feedback Already Submitted",
          text: "You can view the submitted feedback below. Resubmission is not available.",
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        })
      }
    } else {
      setPreviousFeedback(null)
    }

    // Always start fresh
    setForm({ feedback: "", rating: "", recommendation: "" })
  }

  /*
   * Initial load
   */
  useEffect(() => {
    const loadFeedbackDetails = async () => {
      setLoading(true)

      if (isDemoToken) {
        applyFeedbackDetails(demoFeedbackDetails)
        setLoading(false)
        return
      }

      try {
        const response = await axios.get(
          `/api/v1/interview-feedback/${token}/`
        )
        applyFeedbackDetails(response.data, true)
      } catch (error) {
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          "Feedback link is invalid or expired."

        Swal.fire("Error", errorMessage, "error")
        setDetails(null)
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      loadFeedbackDetails()
    }
  }, [token, isDemoToken])

  const interviewTime = useMemo(() => {
    if (!details) return "--"
    const time = [details.start_time, details.end_time]
      .filter(Boolean)
      .join(" - ")
    return [details.interview_date, time].filter(Boolean).join(" | ")
  }, [details])

  const panelOptions = useMemo(
    () => normalizePanelOptions(details),
    [details]
  )

  /*
   * Merge server candidate_options with local submitted state
   */
  const candidateOptions = useMemo(() => {
    if (!details?.candidate_options) return []

    const raw = Array.isArray(details.candidate_options)
      ? details.candidate_options
      : []

    return raw.map(c => ({
      ...c,
      submitted_at:
        localSubmitted[String(c.job_candidate_id)] ||
        c.submitted_at ||
        null,
    }))
  }, [details, localSubmitted])

  const selectedCandidate = useMemo(
    () =>
      candidateOptions.find(
        c => String(c.job_candidate_id) === selectedCandidateId
      ) || null,
    [candidateOptions, selectedCandidateId]
  )

  const selectedPanel = useMemo(
    () =>
      panelOptions.find(
        p =>
          (p.panel_id != null ? String(p.panel_id) : p.name) ===
          selectedPanelId
      ) || null,
    [panelOptions, selectedPanelId]
  )

  /*
   * Central candidate-select logic — used by the preview cards.
   * No API call; the preview data (rating/recommendation/feedback)
   * already lives on candidateOptions so switching is instant.
   */
  const selectCandidate = candidateId => {
    setSelectedCandidateId(candidateId)
    resetForm()

    if (!candidateId) return

    const candidate = candidateOptions.find(
      item => String(item.job_candidate_id) === candidateId
    )

    if (!candidate) return

    /*
     * If this candidate already has a submitted_at, surface the
     * previously submitted values (from candidate_options) as a
     * reference block above the form.
     */
    if (candidate.submitted_at) {
      setPreviousFeedback({
        feedback: candidate.feedback || "",
        rating: candidate.rating ?? "",
        recommendation_display: getRecommendationLabel(
          candidate.recommendation_display || candidate.recommendation
        ),
        panel_name: candidate.panel_name || "",
        panel_email: candidate.panel_email || "",
        submitted_at: candidate.submitted_at,
      })
    }
  }

  /*
   * Submit
   */
  const handleSubmit = async e => {
    e.preventDefault()

    if (candidateOptions.length > 1 && !selectedCandidateId) {
      Swal.fire({
        icon: "warning",
        title: "Candidate Required",
        text: "Please select a candidate before submitting feedback.",
      })
      return
    }

    if (candidateOptions.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Candidate Not Available",
        text: "No candidate is available for this interview.",
      })
      return
    }

    if (panelOptions.length > 1 && !selectedPanelId) {
      Swal.fire({
        icon: "warning",
        title: "Panel Required",
        text: "Please select a panel name before submitting feedback.",
      })
      return
    }

    if (panelOptions.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Panel Not Available",
        text: "No interview panel is available.",
      })
      return
    }

    const isCandidateNoShow = form.recommendation === "candidate_no_show"

    if (!isCandidateNoShow && !form.rating) {
      await Swal.fire({
        icon: "warning",
        title: "Rating Required",
        text: "Please provide a rating before submitting feedback.",
      })
      return
    }

    if (!form.recommendation) {
      Swal.fire({
        icon: "warning",
        title: "Recommendation Required",
        text: "Please select a recommendation before submitting feedback.",
      })
      return
    }

    if (!isCandidateNoShow && !form.feedback.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Feedback Required",
        text: "Please enter panel feedback.",
      })
      return
    }

    setSaving(true)

    try {
      /*
       * Demo mode
       */
      if (isDemoToken) {
        setLocalSubmitted(prev => ({
          ...prev,
          [selectedCandidateId]: new Date().toISOString(),
        }))

        Swal.fire({
          icon: "success",
          title: "Preview Saved",
          text: "This is a demo token, so no backend data was changed.",
          showConfirmButton: false,
          timer: 2500,
          timerProgressBar: true,
        })

        resetForm()
        return
      }

      /*
       * POST payload — includes panel_id and job_candidate_id
       */
      const payload = {
        panel_id: selectedPanel?.panel_id || null,
        job_candidate_id: selectedCandidateId,
        rating: form.rating,
        recommendation: form.recommendation,
        feedback: isCandidateNoShow ? form.feedback.trim() : form.feedback,
      }

      const postResponse = await axios.post(
        `/api/v1/interview-feedback/${token}/`,
        payload
      )

      /*
       * Update candidate_options from the POST response so that
       * rating / recommendation_display / feedback / submitted_at
       * are all up-to-date for every candidate (used by both the
       * "Previously Submitted" reference block and the preview cards).
       */
      if (postResponse.data?.candidate_options) {
        setDetails(prev => ({
          ...prev,
          candidate_options: postResponse.data.candidate_options,
        }))
      }

      const submittedAt = new Date().toISOString()

      // Keep an immediate local marker even when the POST response returns
      // stale candidate_options without submitted_at for this candidate.
      setLocalSubmitted(prev => ({
        ...prev,
        [selectedCandidateId]: submittedAt,
      }))

      setPreviousFeedback({
        feedback: form.feedback || "",
        rating: form.rating || "",
        recommendation_display: getRecommendationLabel(form.recommendation),
        panel_name: selectedPanel?.name || details.panel_name || "",
        panel_email: selectedPanel?.email || details.panel_email || "",
        submitted_at: submittedAt,
      })

      const responseCandidates = Array.isArray(postResponse.data?.candidate_options)
        ? postResponse.data.candidate_options
        : candidateOptions
      const submittedCandidates = responseCandidates.map(candidate => {
        const candidateId = String(candidate.job_candidate_id)
        return {
          ...candidate,
          submitted_at:
            candidate.submitted_at ||
            localSubmitted[candidateId] ||
            (candidateId === String(selectedCandidateId) ? submittedAt : null),
        }
      })
      const isLastCandidate = submittedCandidates.length > 0 && submittedCandidates.every(candidate => Boolean(candidate.submitted_at))

      const companyLogo = bharatAtsLogo
      if (isLastCandidate) setFeedbackSubmitted(true)

      await Swal.fire({
        title: "",
        html: `
          <div style="text-align:center;padding:8px 8px 2px;">
            <img src="${companyLogo}" alt="Company logo" style="max-width:210px;max-height:86px;object-fit:contain;margin:0 auto 22px;display:block;" />
            <div style="width:42px;height:42px;border-radius:50%;background:#16834b;color:#fff;font-size:26px;line-height:42px;margin:0 auto 16px;font-weight:700;">✓</div>
            <div style="font-size:22px;line-height:1.25;font-weight:700;color:#12344d;">Thank you for submitting feedback.</div>
          </div>
        `,
        customClass: { popup: "feedback-success-alert" },
        showConfirmButton: false,
        showCloseButton: false,
        timer: isLastCandidate ? undefined : 2000,
        timerProgressBar: !isLastCandidate,
        backdrop: false,
        didOpen: popup => {
          popup.style.border = "2px solid #16834b"
          popup.style.borderRadius = "18px"
          popup.style.boxShadow = "0 24px 70px rgba(0, 0, 0, 0.28)"
          const closeButton = popup.querySelector(".swal2-close")
          if (closeButton) closeButton.remove()
        },
      })

      if (!isLastCandidate) {
        const nextCandidate = submittedCandidates.find(
          candidate => !candidate.submitted_at
        )
        if (nextCandidate) {
          selectCandidate(String(nextCandidate.job_candidate_id))
        }
      }

      /*
       * Clear the editable fields while keeping the submitted details visible
       */
      setForm({ feedback: "", rating: "", recommendation: "" })
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to submit feedback.",
        "error"
      )
    } finally {
      setSaving(false)
    }
  }

  /*
   * Loading state
   */
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f6f8fb",
        }}
      >
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  /*
   * Invalid / expired link
   */
  if (!details) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: "#f6f8fb",
          padding: 24,
        }}
      >
        <div
          style={{
            maxWidth: 520,
            textAlign: "center",
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: 28,
          }}
        >
          <img
            src={bharatAtsLogo}
            alt="Bharat ATS"
            style={{ maxWidth: 190, maxHeight: 78, objectFit: "contain", marginBottom: 22 }}
          />
          <h3 style={{ margin: 0, color: "#0f172a" }}>
            Feedback Link Not Available
          </h3>
          <p style={{ margin: "10px 0 0", color: "#64748b" }}>
            Please contact the recruitment team for a fresh feedback link.
          </p>
        </div>
      </div>
    )
  }

  if (feedbackSubmitted) return null

  const totalCandidates = candidateOptions.length
  const submittedCount = candidateOptions.filter(c => c.submitted_at).length
  const allDone = totalCandidates > 0 && submittedCount === totalCandidates

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#eef3f8",
        padding: "30px 16px",
      }}
    >
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div
          style={{
            background: "#0e527d",
            color: "#fff",
            borderRadius: "10px 10px 0 0",
            padding: "26px 30px",
            boxShadow: "0 14px 35px rgba(15, 82, 125, 0.16)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              fontWeight: 700,
              opacity: 0.9,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#9dd8ff",
                display: "inline-block",
              }}
            />
            Interview Panel Feedback
          </div>

          <h1
            style={{
              margin: "10px 0 0",
              fontSize: 22,
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            {details.job_title || "Job"}
          </h1>

          {/* Multi-candidate progress indicator */}
          {totalCandidates > 1 && (
            <div
              style={{
                marginTop: 8,
                fontSize: 13,
                opacity: 0.85,
                fontWeight: 600,
              }}
            >
              {submittedCount} / {totalCandidates} candidates reviewed
              {allDone && " ✅ All done!"}
            </div>
          )}
        </div>

        {/* ── BODY ────────────────────────────────────────────────── */}
        <div
          style={{
            background: "#fff",
            border: "1px solid #dbe3ef",
            borderTop: 0,
            borderRadius: "0 0 10px 10px",
            padding: 28,
            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
          }}
        >
          {/* ── ALL CANDIDATES PREVIEW ─────────────────────────────
              Every candidate shown up front with name + rating, so
              the panel doesn't have to select one blind before
              seeing who's done / what they scored. Tap a card to
              load that candidate into the form below. ─────────── */}
          {candidateOptions.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#61738f",
                  textTransform: "uppercase",
                  marginBottom: 8,
                }}
              >
                Candidates ({submittedCount}/{totalCandidates} reviewed)
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                {candidateOptions.map(candidate => (
                  <CandidateCard
                    key={candidate.job_candidate_id}
                    candidate={candidate}
                    isSelected={
                      String(candidate.job_candidate_id) ===
                      selectedCandidateId
                    }
                    onSelect={() =>
                      selectCandidate(String(candidate.job_candidate_id))
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── DETAILS GRID ───────────────────────────────────────── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 14,
              alignItems: "stretch",
            }}
          >
            <DetailItem
              label="Job"
              value={`${details.job_title || ""}${
                details.job_number ? ` (${details.job_number})` : ""
              }`}
            />

            <DetailItem
              label="Selected Candidate"
              value={selectedCandidate?.name}
            />

            <DetailItem label="Interview Time" value={interviewTime} />
            <DetailItem label="Company" value={details.company_name} />
            <DetailItem
              label="Interview Type"
              value={[details.interview_type, details.interview_sub_type]
                .filter(Boolean)
                .join(" - ")}
            />
            <DetailItem
              label="Candidate Email"
              value={details.candidate_email}
            />
          </div>

          {/* ── STEP 5: PREVIOUSLY SUBMITTED REFERENCE ─────────────── */}
          {previousFeedback && (
            <div
              style={{
                marginTop: 24,
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                padding: 18,
                background: "#eff6ff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                <div>
                  <div
                    style={{ fontSize: 14, fontWeight: 800, color: "#1e40af" }}
                  >
                    Previously Submitted Feedback
                  </div>
                  <div
                    style={{ fontSize: 12, color: "#3b82f6", marginTop: 2 }}
                  >
                    Submitted on{" "}
                    {new Date(previousFeedback.submitted_at).toLocaleString()}.
                    This feedback has already been submitted and cannot be submitted again.
                  </div>
                </div>

                <span
                  style={{
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    border: "1px solid #93c5fd",
                    borderRadius: 999,
                    padding: "4px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  ✅ Already Submitted
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#3b82f6",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Rating
                  </div>
                  <StarDisplay rating={previousFeedback.rating} />
                </div>

                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#3b82f6",
                      textTransform: "uppercase",
                      marginBottom: 4,
                    }}
                  >
                    Recommendation
                  </div>
                  <div
                    style={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a" }}
                  >
                    {getRecommendationLabel(
                      previousFeedback.recommendation_display
                    ) || "--"}
                  </div>
                </div>

                {previousFeedback.panel_name && (
                  <div>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 800,
                        color: "#3b82f6",
                        textTransform: "uppercase",
                        marginBottom: 4,
                      }}
                    >
                      Panel
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1e3a8a",
                      }}
                    >
                      {previousFeedback.panel_name}
                    </div>
                  </div>
                )}
              </div>

              {previousFeedback.feedback && (
                <div>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#3b82f6",
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    Feedback Notes
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 8,
                      padding: "12px 14px",
                      fontSize: 13,
                      color: "#1e3a8a",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                    }}
                  >
                    {previousFeedback.feedback}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── FEEDBACK FORM — always shown ────────────────────────── */}
          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: 24,
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: 22,
              background: "#fbfdff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 14,
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: 18,
              }}
            >
              <div>
                <div
                  style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}
                >
                  {previousFeedback
                    ? "Feedback Submitted"
                    : "Submit Feedback"}
                  {selectedCandidate?.name
                    ? ` — ${selectedCandidate.name}`
                    : ""}
                </div>
                <div
                  style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}
                >
                  {previousFeedback
                    ? "This feedback is locked and cannot be submitted again."
                    : "Add rating, recommendation, and panel notes for this interview."}
                </div>
              </div>
            </div>

            {/* ── STEP 2 + 3: PANEL / RATING / RECOMMENDATION ───────── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(220px, 1fr) 170px minmax(220px, 1fr)",
                gap: 16,
                marginBottom: 18,
              }}
            >
              {/* STEP 2 — PANEL NAME (pre-selected by panel_id) */}
              <div>
                <label
                  style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
                >
                  Panel Name *
                </label>

                <select
                  className="form-select"
                  value={selectedPanelId}
                  onChange={e => setSelectedPanelId(e.target.value)}
                  disabled={panelOptions.length === 1}
                  style={{ marginTop: 6, height: 40, fontSize: 13 }}
                >
                  {panelOptions.length > 1 && (
                    <option value="">Select panel name</option>
                  )}

                  {panelOptions.map(item => {
                    const val =
                      item.panel_id != null
                        ? String(item.panel_id)
                        : item.name
                    return (
                      <option key={val} value={val}>
                        {item.name}
                      </option>
                    )
                  })}
                </select>

                {panelOptions.length > 1 && !selectedPanelId && (
                  <div
                    style={{
                      marginTop: 6,
                      color: "#dc2626",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Please select a panel.
                  </div>
                )}

                {selectedPanel?.email ? (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: "#64748b",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedPanel.email}
                  </div>
                ) : null}
              </div>

              {/* STEP 3 — RATING (interactive stars) */}
              <div>
                <label
                  style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
                >
                  Rating
                  {form.recommendation !== "candidate_no_show" && (
                    <span style={{ color: "#dc3545" }}> *</span>
                  )}
                </label>

                <div style={{ marginTop: 10, display: "flex", gap: 4 }}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      disabled={Boolean(previousFeedback)}
                      onClick={() =>
                        setForm(prev => ({
                          ...prev,
                          rating:
                            prev.rating === String(star) ? "" : String(star),
                        }))
                      }
                      style={{
                        background: "none",
                        border: "none",
                        padding: "0 2px",
                        cursor: previousFeedback ? "not-allowed" : "pointer",
                        opacity: previousFeedback ? 0.7 : 1,
                        fontSize: 24,
                        color:
                          parseInt(form.rating, 10) >= star
                            ? "#f59e0b"
                            : "#d1d5db",
                        lineHeight: 1,
                        transition: "color 0.1s",
                      }}
                      title={`${star} star${star > 1 ? "s" : ""}`}
                    >
                      &#9733;
                    </button>
                  ))}
                </div>

                {form.rating && (
                  <div style={{ marginTop: 4, fontSize: 11, color: "#64748b" }}>
                    {form.rating} / 5
                  </div>
                )}
              </div>

              {/* STEP 3 — RECOMMENDATION (radio buttons) */}
              <div>
                <label
                  style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
                >
                  Recommendation <span style={{ color: "#dc3545" }}>*</span>
                </label>

                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  {recommendationOptions
                    .filter(opt => opt.value !== "")
                    .map(opt => (
                      <label
                        key={opt.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          cursor: "pointer",
                          fontSize: 13,
                          fontWeight:
                            form.recommendation === opt.value ? 700 : 500,
                          color:
                            form.recommendation === opt.value
                              ? "#0f172a"
                              : "#475569",
                        }}
                      >
                        <input
                          type="radio"
                          name="recommendation"
                          value={opt.value}
                          checked={form.recommendation === opt.value}
                          disabled={Boolean(previousFeedback)}
                          onChange={e =>
                            setForm(prev => ({
                              ...prev,
                              rating:
                                e.target.value === "candidate_no_show"
                                  ? ""
                                  : prev.rating,
                              recommendation: e.target.value,
                            }))
                          }
                          style={{ accentColor: "#0e527d" }}
                        />
                        {opt.label}
                      </label>
                    ))}
                </div>
              </div>
            </div>

            {/* STEP 3 — FEEDBACK TEXTAREA */}
            <label
              style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}
            >
              Panel Feedback
              {form.recommendation === "candidate_no_show" ? "" : " *"}
            </label>

            <textarea
              className="form-control"
              rows={8}
              value={form.feedback}
              disabled={Boolean(previousFeedback)}
              onChange={e =>
                setForm(prev => ({ ...prev, feedback: e.target.value }))
              }
              placeholder="Write interview notes, strengths, concerns, and next-step recommendation."
              style={{
                marginTop: 6,
                resize: "vertical",
                fontSize: 13,
                minHeight: 180,
                background: previousFeedback ? "#f8fafc" : "#fff",
                color: previousFeedback ? "#64748b" : "#0f172a",
              }}
            />

            {form.recommendation !== "candidate_no_show" &&
              !form.feedback.trim() && (
              <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                Feedback is required before submitting.
              </div>
            )}

            {/* STEP 4 — SUBMIT */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}
            >
              {!previousFeedback && (
                <button
                  type="submit"
                  className="btn"
                  disabled={saving || switchingCandidate}
                  style={{
                    background: "#0e527d",
                    color: "#fff",
                    minWidth: 190,
                    height: 44,
                    fontSize: 13,
                    fontWeight: 800,
                    borderRadius: 7,
                    boxShadow: "0 10px 22px rgba(14, 82, 125, 0.22)",
                  }}
                >
                  {saving ? "Saving…" : "Submit Feedback"}
                </button>
              )}
            </div>
          </form>

          {/* STEP 5 — ALL DONE BANNER */}
          {allDone && (
            <div
              style={{
                marginTop: 20,
                background: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: 10,
                padding: "16px 20px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 4 }}>&#127881;</div>
              <div
                style={{ fontSize: 14, fontWeight: 800, color: "#166534" }}
              >
                All candidates reviewed!
              </div>
              <div style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>
                You have submitted feedback for all {totalCandidates} candidate
                {totalCandidates > 1 ? "s" : ""}. Thank you!
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default InterviewFeedbackPage
